import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const DEFAULT_API_BASE_URL = "https://api-saas.mend.io/api/v3.0";

const apiBaseUrl = (baseUrl: string | undefined) =>
  (baseUrl?.trim() || DEFAULT_API_BASE_URL).replace(/\/+$/, "");

export const mend = defineConnector({
  identifier: "mend",
  name: "Mend.io",
  tagLine: "Application security and SCA",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ baseUrl }) => exactUrlToRegex(apiBaseUrl(baseUrl)),
  auth: customAuth("mend"),
  steps: [
    {
      title: "Create Mend API credentials",
      items: [
        {
          kind: "markdown",
          content:
            "Use a Mend service user key for unattended workflows. 3B signs in and refreshes Mend’s short-lived JWT automatically. A personal user key also works.",
        },
      ],
    },
    {
      title: "Enter your Mend API details",
      items: [
        {
          kind: "url",
          label: "API base URL",
          required: true,
          defaultValue: DEFAULT_API_BASE_URL,
          placeholder: DEFAULT_API_BASE_URL,
          helperText:
            "Use the API 3.0 base URL for your Mend instance, including /api/v3.0.",
          target: { kind: "configuration", field: "baseUrl" },
        },
        {
          kind: "input",
          label: "Organization UUID",
          required: true,
          helperText:
            "Copy this from Mend Platform → Administration → General configuration → Organization UUID.",
          target: { kind: "configuration", field: "orgUuid" },
        },
        {
          kind: "input",
          label: "User email",
          required: true,
          helperText:
            "For a service user, use the generated email shown for that user in Mend.",
          target: { kind: "configuration", field: "email" },
        },
        {
          kind: "input",
          label: "User key",
          required: true,
          helperText:
            "For automation, Mend recommends a service user token from Administration → Users → Copy token. You can also use a personal key from My profile → User keys.",
          target: { kind: "secrets", field: "userKey" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl, orgUuid }) => ({
      url: `${apiBaseUrl(baseUrl)}/orgs/${encodeURIComponent(orgUuid)}/applications?limit=1`,
      method: "GET",
    }),
  },
});
